import type { Address } from '@prisma/client';
import { ScheduledJobType } from '@prisma/client';

export const classNames = (...classes: (string | boolean | undefined | null)[]) => {
	return classes.filter((val) => !!val).join(' ');
};

export function capitalizeFirstLetter(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export const formatAddress = ({
	address,
	includeNewline = true,
}: {
	address: Pick<Address, 'street1' | 'street2' | 'city' | 'state' | 'zip'>;
	includeNewline?: boolean;
}) => {
	const { street1, street2, city, state, zip } = address;
	return `${street1}, ${street2 ? `${street2}, ` : ''}${includeNewline ? `\n` : ``}${city}, ${state} ${zip}`;
};

export const capitalizeEveryWord = ({ text, delimiters = [' ', '-'] }: { text: string; delimiters?: string[] }) => {
	let tmpText = text.toLowerCase();

	delimiters.forEach((d) => {
		tmpText = tmpText.split(d).map(capitalizeFirstLetter).join(d);
	});

	return tmpText;
};

export function googleURLFormatter(address: Address, useCoords = false) {
	if (useCoords && address.latitude && address.longitude) {
		return `https://maps.google.com/?q=${address.latitude},${address.longitude}`;
	} else {
		//Throw it to google to do the right thing if we don't have a map location
		return `https://maps.google.com/?q=${formatAddress({ address })}`;
	}
}

export function formatStageName(stage: string) {
	return capitalizeEveryWord({ text: stage.split('_').join(' ') });
}

export function getFullName<T extends { firstName: string; lastName: string }>(user: T) {
	return `${user.firstName} ${user.lastName}`;
}

/**
 * Returns the user's full name with their optional internal accounting (payroll) ID appended after a dash,
 * e.g. "Jane Smith - PR-1029". Falls back to the plain full name when no ID is set. Used on time-tracking
 * reports and on-screen time-tracking views.
 */
export function getFullNameWithInternalAccountingId<T extends { firstName: string; lastName: string; internalAccountingId?: string | null }>(
	user: T
) {
	const fullName = getFullName(user);
	return user.internalAccountingId ? `${fullName} - ${user.internalAccountingId}` : fullName;
}

type IconFromSJType<T extends { jobType: ScheduledJobType }> = T;

export const iconFromScheduledJob = <T extends { jobType: ScheduledJobType }>(scheduledJob?: IconFromSJType<T>) => {
	if (!scheduledJob?.jobType) return 'calendar';
	switch (scheduledJob.jobType) {
		case ScheduledJobType.FABRICATION:
			return 'wrench';
		case ScheduledJobType.INSTALL:
			return 'window-frame';
		case ScheduledJobType.MEASURE:
			return 'ruler-combined';
		case ScheduledJobType.SERVICE:
			return 'hammer';
		case ScheduledJobType.OTHER:
			return 'calendar-pen';
	}
};
